import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonEdit, CardV2 as Card, Label, Text } from '@feathr/components';

import AdGroupKeywords from '../../AdStep/AdGroupPreview/AdGroupKeywords';
import AdGroupSitelinks from '../../AdStep/AdGroupPreview/AdGroupSitelinks';
import type { IAdGroup } from '../../AdStep/AdStep.types';
import AdReviewCard from './AdReviewCard';

interface IAdGroupReviewCardProps {
  readonly adGroup: IAdGroup;
  /** Position in the list. Names the card, since ad group names aren't unique. */
  readonly index: number;
  /** Jumps to the Ads step and opens this ad group for editing there. */
  readonly onEdit: (adGroupKey: string) => void;
  /** The advertiser name Google shows above each ad's display URL. */
  readonly businessName?: string;
}

/**
 * One ad group as it reads on the Review step: the keywords and sitelinks its ads share,
 * then each ad.
 *
 * Sitelinks are absent from the design but included here — they are ad-group-level
 * configuration this step exists to check, and the ad previews show only their labels.
 *
 * @param props - {@link IAdGroupReviewCardProps}.
 */
function AdGroupReviewCard({
  adGroup,
  index,
  onEdit,
  businessName,
}: Readonly<IAdGroupReviewCardProps>): JSX.Element {
  const { t } = useTranslation();
  const name = `review-ad-group-${index + 1}`;

  function handleEdit(): void {
    onEdit(adGroup.key);
  }

  return (
    <Card name={name}>
      <Card.Header title={adGroup.name.trim() || t('Untitled ad group')}>
        <ButtonEdit onClick={handleEdit} t={t} />
      </Card.Header>
      <Card.Content addVerticalGap={true}>
        <Card name={`${name}-keywords`} width={'full'}>
          <Card.Header padding={'compact'} title={t('Keywords')} titleEl={'h4'} />
          <Card.Content addVerticalGap={true}>
            <div data-name={`${name}-included-keywords`}>
              <Label>{t('Included keywords')}</Label>
              {/* Match types are shown because they change which searches the ad reaches. */}
              <AdGroupKeywords keywords={adGroup.keywords} showMatchType={true} />
            </div>
            <div data-name={`${name}-excluded-keywords`}>
              <Label>{t('Excluded keywords')}</Label>
              <AdGroupKeywords keywords={adGroup.negativeKeywords} />
            </div>
          </Card.Content>
        </Card>
        <Card name={`${name}-sitelinks`} width={'full'}>
          <Card.Header
            description={t('Shown beneath every ad in this ad group.')}
            padding={'compact'}
            title={t('Sitelinks')}
            titleEl={'h4'}
          />
          <Card.Content>
            <AdGroupSitelinks sitelinks={adGroup.sitelinks} />
          </Card.Content>
        </Card>
        {adGroup.ads.length ? (
          adGroup.ads.map((ad, adIndex) => (
            <AdReviewCard
              ad={ad}
              adNumber={adIndex + 1}
              businessName={businessName}
              key={ad.key}
              name={`${name}-ad-${adIndex + 1}`}
              sitelinks={adGroup.sitelinks}
            />
          ))
        ) : (
          <Text.SmallRegular>{t('No ads yet.')}</Text.SmallRegular>
        )}
      </Card.Content>
    </Card>
  );
}

export default AdGroupReviewCard;
